import React from "react";
import { Instagram, Phone, MapPin, Mail } from "lucide-react";
import { Mandala, BorderOrnament, Lotus } from "./IndianMotifs";

export default function Footer({ onBook }) {
  return (
    <footer id="visit" className="relative bg-sage-900 text-cream pt-20 pb-10 overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />
      <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-gold-500/15 blur-3xl" />

      <div className="container mx-auto px-5 md:px-10 relative z-10">
        <div className="grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 lg:col-span-6">
            <div className="text-[11px] uppercase tracking-[0.32em] text-gold-200/80 mb-4">Visit — 05</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.98] tracking-tightest">
              We welcome <span className="font-script text-gold-300">new</span><br />patients.
            </h2>
            <div className="mt-4 text-gold-300/70"><BorderOrnament width={220} /></div>
            <p className="mt-6 max-w-lg text-cream/70 leading-relaxed">
              Folded with care for our local community. Reach out — we'll help you book the right kind of visit.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <ContactItem icon={<Phone className="w-4 h-4" />} label="Call" value="+1 (555) 123-4567" href="tel:+15551234567" />
              <ContactItem icon={<Instagram className="w-4 h-4" />} label="Instagram" value="@dentist" href="https://instagram.com/dentist" />
              <ContactItem icon={<MapPin className="w-4 h-4" />} label="Address" value="123 Dental St, Cityville" href="https://maps.google.com/?q=123+Dental+St,+Cityville" />
              <ContactItem icon={<Mail className="w-4 h-4" />} label="Email" value="info@example.com" href="mailto:info@example.com" />
            </div>

            <button
              onClick={onBook}
              data-testid="footer-book-btn"
              data-cursor="hover"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-gold-500 text-sage-900 pl-7 pr-2 py-2 btn-glow"
            >
              <span className="uppercase text-[12px] tracking-[0.28em]">Book a Consultation</span>
              <span className="grid place-items-center w-11 h-11 rounded-full bg-sage-900 text-cream">→</span>
            </button>
          </div>

          <div className="col-span-12 lg:col-span-6">
            <div className="relative rounded-[28px] overflow-hidden ring-1 ring-gold-300/20 aspect-[4/3] lg:aspect-square">
              <iframe
                title="Dentist location"
                src="https://www.google.com/maps?q=123+Dental+St,+Cityville&output=embed"
                className="w-full h-full grayscale-[0.2] contrast-[0.95]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href="https://maps.google.com/?q=123+Dental+St,+Cityville"
                target="_blank" rel="noreferrer"
                data-testid="footer-directions-btn"
                className="absolute bottom-5 right-5 inline-flex items-center gap-2 glass-dark rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-cream"
              >
                Get directions →
              </a>
            </div>
          </div>
        </div>

        {/* Big watermark logo — final brand moment */}
        <div className="mt-24 md:mt-32 flex flex-col items-center text-center reveal">
          <img
            src="/img/logo.png"
            alt="Dentist"
            data-testid="footer-big-logo"
            className="w-[78%] max-w-[720px] h-auto opacity-95 drop-shadow-[0_8px_30px_rgba(201,169,110,0.35)]"
            style={{ filter: "brightness(0) invert(0.95) sepia(0.45) saturate(1.8) hue-rotate(8deg)" }}
          />
          <div className="mt-2 text-[11px] uppercase tracking-[0.42em] text-gold-200/80">
            Smiles · Dental · Care
          </div>
        </div>

        {/* Honest disclaimer */}
        <div className="mt-14 max-w-3xl mx-auto text-center" data-testid="footer-disclaimer">
          <div className="text-[10px] uppercase tracking-[0.42em] text-gold-200/70 mb-3">A gentle note</div>
          <p className="text-[13px] md:text-sm leading-relaxed text-cream/65 font-display italic">
            Dentist is a dental clinic led by <span className="not-italic text-cream/80">Dr. SN and Dr. KB</span>.
            All treatments are performed following established dental protocols and guidelines.
            For conditions requiring specialist referral, we'll gladly connect you with the right expert.
          </p>
          <p className="mt-3 text-[11px] text-cream/45 tracking-wide">
            Results vary by individual · Consultation required before any procedure · No image on this site is a guaranteed outcome.
          </p>
        </div>

        <div className="mt-16 pt-8 border-t border-cream/10 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] uppercase tracking-[0.28em] text-cream/55">
          <div className="flex items-center gap-3">
            © {new Date().getFullYear()} Dentist
          </div>
          <div className="flex items-center gap-6">
            <span>Smiles · Precision · Care</span>
            <span className="text-gold-300/70">✦</span>
            <span>Folded with care in Cityville</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function ContactItem({ icon, label, value, href }) {
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-cursor="hover" className="block group">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold-200/80">
        {icon} {label}
      </div>
      <div className="mt-2 font-display text-lg text-cream group-hover:text-gold-200 transition-colors">{value}</div>
    </a>
  );
}
